import { defineComponent } from "vue";
import { mapState } from "pinia";
import { Container } from "aurelia-dependency-injection";
import { required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

import { useConvertToPendingPal } from "../../store/useConvertToPendingPal";
import { ProvidersService } from "../../../../../services/providers-service";
import { PhysiciansService } from "../../../../../services/physician-service";
import { ApplicationEnum } from "../../../../../enums/ApplicationEnum";
import { LookupService } from "../../../../../services/lookup-service";
import { IPhysicianInformation } from "../../../../../resources-vue/vue-interfaces/i-get-physician";
import { formatName } from "../../../../../common/format-name";

import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import ContactInfoCard from "../../../../../resources-vue/vue-elements/ContactInfoCard/ContactInfoCard.vue";
import ContactInfoLoading from "../../../../../resources-vue/vue-custom-elements/ContactInfoLoading/ContactInfoLoading.vue";

export default defineComponent({
    components: {
        FormSection,
        TypeaheadInput,
        ContactInfoCard,
        ContactInfoLoading
    },
    validations() {
        return {
            patientDetails: {
                providers: {
                    palliativeProviderId: { required }
                }
            }
        };
    },

    data() {
        return {
            _providersService: null,
            _physicianService: null,
            _lookupService: null,
            primaryCareProvider: { value: "", name: "" },
            primaryCareProviderInfo: {} as IPhysicianInformation,
            palliativeProvider: { value: "", name: "" },
            palliativeApplication: ApplicationEnum.AxxessPalliative,
            palliativeCareProviderInfo: {} as IPhysicianInformation,
            referringProvider: { value: "", name: "" },
            referringProviderInfo: {} as IPhysicianInformation,
            additionalProvider: [] as any,
            additionalProviderInfo: [] as IPhysicianInformation[],
            isLoadingPrimaryCare: false as boolean,
            isLoadingPalliativeCare: false as boolean,
            isLoadingReferringCare: false as boolean,
            isLoadingAdditionalProvider: [] as boolean[],
            formatName,
            v$: useVuelidate()
        };
    },

    created() {
        this._providersService = Container.instance.get(ProvidersService);
        this._lookupService = Container.instance.get(LookupService);
        this._physicianService = Container.instance.get(PhysiciansService);
    },

    async mounted() {
        if (this.patientDetails.providers.primaryCareProviderId) {
            this.isLoadingPrimaryCare = true;
            this.primaryCareProviderInfo = await this.getPhysicianById(
                this.patientDetails.providers.primaryCareProviderId
            );
            this.isLoadingPrimaryCare = false;
            this.primaryCareProvider = {
                name: formatName(this.primaryCareProviderInfo.firstName, this.palliativeCareProviderInfo.lastName),
                value: this.palliativeCareProviderInfo.id
            };
        }
        if (this.patientDetails.providers.palliativeProviderId) {
            this.isLoadingPalliativeCare = true;
            this.palliativeCareProviderInfo = await this.getPhysicianById(
                this.patientDetails.providers.palliativeProviderId
            );
            this.isLoadingPalliativeCare = false;
            this.palliativeProvider = {
                name: formatName(this.palliativeCareProviderInfo.firstName, this.palliativeCareProviderInfo.lastName),
                value: this.palliativeCareProviderInfo.id
            };
        }
        if (this.patientDetails.providers.referringProviderId) {
            this.isLoadingReferringCare = true;
            this.referringProviderInfo = await this.getPhysicianById(this.patientDetails.providers.referringProviderId);
            this.isLoadingReferringCare = false;
            this.referringProvider = {
                name: formatName(this.referringProviderInfo.firstName, this.referringProviderInfo.lastName),
                value: this.referringProviderInfo.id
            };
        }
        if (this.patientDetails.providers.additionalProviderIds) {
            this.isLoadingReferringCare = true;
            this.patientDetails.providers.additionalProviderIds.forEach(async (item: string) => {
                const providerInfo = await this.getPhysicianById(item);
                this.additionalProvider.push({
                    name: `${providerInfo.lastName}, ${providerInfo.firstName}`,
                    value: providerInfo.id
                });
                this.additionalProviderInfo.push(providerInfo);
                return providerInfo;
            });
            this.isLoadingReferringCare = false;
        }
    },

    computed: {
        ...mapState(useConvertToPendingPal, ["patientDetails", "application", "accountId", "patientOptions"])
    },

    methods: {
        async getPhysicianList(filter: string, limit: number, index?: number) {
            let data = await this._physicianService.getPhysiciansByName({
                providerIds: [""],
                application: this.application,
                page: 1,
                pageLength: limit,
                name: filter,
                accountId: this.accountId
            });
            return data?.items.map((item: any) => {
                return {
                    name: item.name,
                    value: item.id
                };
            });
        },

        async getPhysicianById(physicianId: string) {
            const payload = {
                physicianId: physicianId,
                application: this.application,
                accountId: this.accountId
            };
            let physician = await this._physicianService.getPhysicianById(payload);
            return physician;
        },

        async attendingPhysicianChangedPrimaryCare(newValue: any) {
            if (newValue != null) {
                this.patientDetails.providers.primaryCareProviderId = newValue.value;
                this.primaryCareProvider.value = newValue.value;
                this.primaryCareProvider.name = newValue.name;
                this.isLoadingPrimaryCare = true;
                this.primaryCareProviderInfo = await this.getPhysicianById(this.primaryCareProvider.value);
                this.isLoadingPrimaryCare = false;
            }
        },

        async attendingPhysicianChangedPalliativeCare(newValue: any) {
            if (newValue != null) {
                this.patientDetails.providers.palliativeProviderId = newValue.value;
                this.palliativeProvider.name = newValue.name;
                this.palliativeProvider.value = newValue.value;
                this.isLoadingPalliativeCare = true;
                this.palliativeCareProviderInfo = await this.getPhysicianById(this.palliativeProvider.value);
                this.isLoadingPalliativeCare = false;
            }
        },

        async attendingPhysicianChangedReferring(newValue: any) {
            if (newValue != null) {
                this.patientDetails.providers.referringProviderId = newValue.value;
                this.referringProvider.name = newValue.name;
                this.referringProvider.value = newValue.value;
                this.isLoadingReferringCare = true;
                this.referringProviderInfo = await this.getPhysicianById(this.referringProvider.value);
                this.isLoadingReferringCare = false;
            }
        },

        async attendingPhysicianChangedOther(newValue: any, index: number) {
            this.additionalProvider[index] = { name: newValue.name, value: newValue.value };
            this.isLoadingAdditionalProvider[index] = true;
            this.additionalProviderInfo[index] = await this.getPhysicianById(newValue.value);
            this.isLoadingAdditionalProvider[index] = false;
            this.patientDetails.providers.additionalProviderIds = this.additionalProvider.map(
                (item: any) => item.value
            );
        },

        async addProvider() {
            this.additionalProvider.push({ value: "", name: "" });
            this.isLoadingAdditionalProvider.push(false);
            this.additionalProviderInfo.push({
                address: {
                    accountId: "",
                    addressCity: "",
                    addressCounty: "",
                    addressLatitude: "",
                    addressLine1: "",
                    addressLine2: "",
                    addressLongitude: "",
                    addressStateCode: "",
                    addressZipCodeFive: "",
                    addressZipCodeFour: "",
                    country: "",
                    isBypassed: false,
                    isValidated: false
                },
                associatedAgencyProviderIds: [""],
                communityLiaisonId: "",
                communityLiaisonName: "",
                credentials: "",
                deliveryMethod: null,
                deliveryVendor: null,
                email: "",
                expirationDate: "",
                faxNumber: "",
                firstName: "",
                id: "",
                isGrantedAccess: false,
                isNotInExclusionList: false,
                isPecosVerified: false,
                lastName: "",
                licenseNumber: "",
                medicaidProviderNumber: "",
                middleInitial: "",
                npiNumber: "",
                phoneNumbers: {
                    id: "",
                    number: "",
                    extension: "",
                    type: null,
                    isPrimary: false,
                    afterHours: false,
                    country: "",
                    ordinal: null,
                    ordinalNumber: null
                },
                primaryState: "",
                secondaryExpirationDate: "",
                secondaryLicenseNumber: "",
                secondaryState: "",
                taxonomyCode: ""
            });
        }
    }
});
